/* ==========================================================================
   FUEL MOTION controller.

   RJ, 2026-09-01: "Need more motion animations and micro interactions."

   TWO JOBS, AND THE FIRST ONE IS THE SAFETY INTERLOCK:

     1. Add `.fuel-motion` to <body>, and ONLY if the reader has not asked for
        reduced motion. Every hide-then-reveal rule in fuel-motion.css is
        nested under that class, so a reader who never gets this class - script
        blocked, script failed, reduced motion on - sees a completely normal
        page with nothing hidden. The usual arrangement is the other way round
        and takes the whole page down with it when the script does not run.

     2. Tag the things that should rise, and unhide each as it is scrolled to.

   WHAT GETS TAGGED, AND WHY IT IS A LIST AND NOT "EVERY CHILD"
   Animating every element in a section produces a page that shimmers for two
   seconds on every scroll and reads as a template. The list below is the top
   of each block plus the repeating cards, which is the structure a reader is
   actually parsing. Anything already inside a fuel-enter sequence (the PDP buy
   column) is excluded, or it would animate twice and fight itself.
   ========================================================================== */
(function () {
  'use strict';

  var SELECTORS = [
    '.fuel-section > .fuel-wrap > h2',
    '.fuel-box__head',
    '.fuel-box__panel',
    '.fuel-box__cell',
    '.fuel-card',
    '.fuel-figure',
    '.fuel-prose__grid > *',
    '.fuel-tiles__tile',
    '.fuel-ingredients__list',
    '.fuel-macros',
    '.fuel-plan--soon'
    /* 2026-09-02, RJ item 4 restructure: the new problem/facts/tray sections
       on templates/page.fuel.json (fuel-prose and fuel-figure again, with a
       fresh instance id each) ride the same two selectors above by class,
       so nothing new is needed here for them specifically - listed for the
       next reader who greps this file wondering where they are covered.
       The carousel nav buttons are deliberately NOT in this list: they
       start `hidden` and are unhidden by initCarousels() below rather than
       by scroll position, so tagging them here would risk an unhidden but
       still opacity:0 button sitting under the pointer before its own
       IntersectionObserver entry ever fires. */
  ];

  // Stagger, capped. Card 14 of a 20-card grid must not wait 1.4 seconds; past
  // the sixth item the delay stops growing and the rest arrive together, which
  // is what the eye expects once a row is off screen anyway.
  var STEP = 60;
  var MAX_STEPS = 4;

  function init() {
    var body = document.body;
    if (!body || body.className.indexOf('fuel-world') === -1) return;

    var reduced = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;

    if (!('IntersectionObserver' in window)) return;

    body.classList.add('fuel-motion');

    var nodes = [];
    SELECTORS.forEach(function (selector) {
      var found = document.querySelectorAll('body.fuel-world ' + selector);
      for (var i = 0; i < found.length; i++) {
        if (found[i].closest('.fuel-pdp__detail')) continue;   /* see note above */
        if (found[i].hasAttribute('data-fuel-rise')) continue;
        nodes.push(found[i]);
      }
    });

    if (!nodes.length) {
      // Nothing to reveal on this template. Drop the class again rather than
      // leaving it on: it is also what gates the reduced-motion overrides, and
      // an unused hook on <body> is a thing the next person has to explain.
      body.classList.remove('fuel-motion');
      return;
    }

    nodes.forEach(function (node) { node.setAttribute('data-fuel-rise', ''); });

    var seen = 0;
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var step = Math.min(seen, MAX_STEPS);
        entry.target.style.setProperty('--rise-delay', (step * STEP) + 'ms');
        entry.target.setAttribute('data-shown', '');
        observer.unobserve(entry.target);   /* reveal once, never re-hide */
        seen++;
      });
      // The stagger counter resets between screenfuls, so the second section
      // down the page starts from zero rather than inheriting a half-second
      // delay from everything above it.
      window.clearTimeout(init._reset);
      init._reset = window.setTimeout(function () { seen = 0; }, 240);
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });

    nodes.forEach(function (node) { observer.observe(node); });

    // v2 (2026-09-03): a hard backstop. Whatever the observer does, every
    // tagged node is shown 900ms after tagging, and every scroll frame shows
    // anything inside the viewport by plain geometry. Late by a beat at
    // worst; never missing.
    window.setTimeout(function () {
      nodes.forEach(function (node) { node.setAttribute('data-shown', ''); });
    }, 900);
    var raf = 0;
    function sweep() {
      raf = 0;
      var h = window.innerHeight || document.documentElement.clientHeight;
      nodes.forEach(function (node) {
        if (node.hasAttribute('data-shown')) return;
        var box = node.getBoundingClientRect();
        if (box.top < h && box.bottom > 0) node.setAttribute('data-shown', '');
      });
    }
    function onScroll() { if (!raf) raf = window.requestAnimationFrame(sweep); }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    // Anything already on screen at load reveals on the next frame rather than
    // waiting for a scroll that may never come on a short page. The observer
    // fires immediately for those, so this is only a guard for the case where
    // the first callback lands before paint.
    window.requestAnimationFrame(function () {
      nodes.forEach(function (node) {
        var box = node.getBoundingClientRect();
        if (box.top < window.innerHeight && box.bottom > 0) {
          node.setAttribute('data-shown', '');
        }
      });
    });
  }

  /* ==========================================================================
     CAROUSEL CONTROLLER (RJ item 6, 2026-09-02: "a real carousel where the
     range currently sits as a static grid ... a swipeable, snap-scrolling
     rail with keyboard and screen reader support").

     Functional, not decorative, so unlike init() above this runs REGARDLESS
     of prefers-reduced-motion and does not require IntersectionObserver -
     the base markup (fuel.css .fuel-carousel__track) already scrolls and
     snaps with zero JS, on touch, trackpad and Tab. This function only adds
     the two pointer-device arrow buttons and keyboard arrow-key support on
     top of that, so its own failure degrades to the same native scrollable
     row, never to nothing.
     ========================================================================== */
  function initCarousels() {
    var reduced = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var behavior = reduced ? 'auto' : 'smooth';

    var rails = document.querySelectorAll('[data-fuel-carousel]');
    for (var i = 0; i < rails.length; i++) {
      (function (rail) {
        var track = rail.querySelector('[data-fuel-carousel-track]');
        var prev = rail.querySelector('[data-fuel-carousel-prev]');
        var next = rail.querySelector('[data-fuel-carousel-next]');
        if (!track || !prev || !next) return;

        function step() {
          var card = track.querySelector('.fuel-card-wrap');
          if (!card) return track.clientWidth * 0.8;
          var style = window.getComputedStyle(track);
          var gap = parseFloat(style.columnGap || style.gap || '0') || 0;
          return card.getBoundingClientRect().width + gap;
        }

        function refresh() {
          var max = track.scrollWidth - track.clientWidth;
          if (max <= 4) {
            // Nothing to scroll (e.g. a narrow collection on a wide
            // viewport): stay hidden rather than show two dead buttons.
            prev.hidden = true;
            next.hidden = true;
            return;
          }
          prev.hidden = false;
          next.hidden = false;
          prev.disabled = track.scrollLeft <= 4;
          next.disabled = track.scrollLeft >= max - 4;
        }

        prev.addEventListener('click', function () {
          track.scrollBy({ left: -step(), behavior: behavior });
        });
        next.addEventListener('click', function () {
          track.scrollBy({ left: step(), behavior: behavior });
        });

        // Keyboard support beyond native Tab-to-card scrolling: with the
        // track itself focused (role="region" tabindex="0" in the markup),
        // arrow keys page the rail one card at a time. preventDefault only
        // when a scroll actually happens, so a rail already at either end
        // never swallows the key (e.g. Left at the start still lets the
        // reader tab/scroll the page normally).
        track.addEventListener('keydown', function (event) {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          var max = track.scrollWidth - track.clientWidth;
          if (max <= 4) return;
          if (event.key === 'ArrowLeft' && track.scrollLeft <= 4) return;
          if (event.key === 'ArrowRight' && track.scrollLeft >= max - 4) return;
          event.preventDefault();
          track.scrollBy({ left: event.key === 'ArrowLeft' ? -step() : step(), behavior: behavior });
        });

        var ticking = false;
        track.addEventListener('scroll', function () {
          if (ticking) return;
          ticking = true;
          window.requestAnimationFrame(function () { refresh(); ticking = false; });
        }, { passive: true });

        window.addEventListener('resize', refresh);
        refresh();
      })(rails[i]);
    }
  }

  /* ==========================================================================
     COUNTER PULSE (RJ item 6, 2026-09-02: micro-interactions on "the
     quantity stepper ... the Stack counter incrementing on the build-your-
     stack page").

     Watches counter-like elements ALREADY rendered by other files
     (sections/fuel-collection.liquid's [data-fuel-pick-qty], which this
     file's own owner renders, plus [data-fuel-box-qty]/[data-fuel-box-count]
     from sections/fuel-box.liquid and [data-fuel-selection-count] from
     snippets/fuel-selection-bar.liquid, neither of which this lane owns)
     with a MutationObserver rather than editing any of those files: a
     text-content change on a matched node gets a one-beat scale pulse via
     the .fuel-counter-pulse class in fuel-motion.css. sections/fuel-box.liquid
     already runs its OWN richer bump/land animations for its own elements
     (see assets/fuel-box.css .is-bumped, .is-won, .is-swapped) - this only
     adds the same feedback to the two counters that had none:
     [data-fuel-pick-qty] on a range/rail card and [data-fuel-selection-count]
     on the bottom bar. Matching the box-builder's own counters too is
     harmless (a second short pulse layered on its existing animation), so
     the selector list is left general rather than hand-excluding files this
     lane does not own.
     ========================================================================== */
  function initCounterPulse() {
    if (!('MutationObserver' in window)) return;
    var SELECTOR = '[data-fuel-pick-qty], [data-fuel-selection-count], ' +
      '[data-fuel-box-qty], [data-fuel-box-count]';
    var nodes = document.querySelectorAll(SELECTOR);
    if (!nodes.length) return;

    function pulse(node) {
      node.classList.remove('fuel-counter-pulse');
      // Force reflow so re-adding the class restarts the animation on a
      // rapid double-tap of the stepper, rather than the second tap being a
      // no-op because the class was already present.
      void node.offsetWidth;
      node.classList.add('fuel-counter-pulse');
    }

    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        var target = m.target.nodeType === 1 ? m.target : m.target.parentElement;
        if (target) pulse(target);
      });
    });

    nodes.forEach(function (node) {
      observer.observe(node, { characterData: true, childList: true, subtree: true });
    });

    // A NEW card can appear later (quick-add stepper markup is already in
    // the DOM at load here, but the bottom bar and the builder tray render
    // rows dynamically). Re-scan on demand rather than polling: any of the
    // buttons that drive these counters is a safe, cheap trigger to re-run
    // the query, since observing an already-observed node twice is a no-op
    // for MutationObserver.
    document.addEventListener('click', function (event) {
      var trigger = event.target.closest &&
        event.target.closest('[data-fuel-pick-more],[data-fuel-pick-less],[data-fuel-box-more],[data-fuel-box-less]');
      if (!trigger) return;
      document.querySelectorAll(SELECTOR).forEach(function (node) {
        observer.observe(node, { characterData: true, childList: true, subtree: true });
      });
    }, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
      initCarousels();
      initCounterPulse();
    });
  } else {
    init();
    initCarousels();
    initCounterPulse();
  }
})();
